import { useCallback, useEffect, useRef, useState } from 'react';

import { FullVideoView, getFullVideo, listFullVideos } from '../services/fleet';

const POLL_MS = 30_000;

export interface StageKey {
  eventId: number;
  stageIndex: number;
}

export interface StageFullVideosState {
  views: Record<string, FullVideoView>;
  setView: (deviceId: string, view: FullVideoView | null, forStage?: StageKey) => void;
  reload: () => void;
}

function isActive(view: FullVideoView | undefined): boolean {
  return view?.state === 'queued' || view?.state === 'running';
}

export function useStageFullVideos(
  eventId: number | null,
  stageIndex: number | null,
): StageFullVideosState {
  const [views, setViews] = useState<Record<string, FullVideoView>>({});
  const paramsRef = useRef({ eventId, stageIndex });
  paramsRef.current = { eventId, stageIndex };

  const isCurrent = useCallback(
    (e: number, s: number) =>
      paramsRef.current.eventId === e && paramsRef.current.stageIndex === s,
    [],
  );

  const setView = useCallback((deviceId: string, view: FullVideoView | null, forStage?: StageKey) => {
    if (forStage && !isCurrent(forStage.eventId, forStage.stageIndex)) return;
    setViews((prev) => {
      const next = { ...prev };
      if (view) next[deviceId] = view;
      else delete next[deviceId];
      return next;
    });
  }, [isCurrent]);

  const load = useCallback(async () => {
    const { eventId: e, stageIndex: s } = paramsRef.current;
    if (e == null || s == null) return;
    try {
      const data = await listFullVideos(e, s);
      if (isCurrent(e, s)) setViews(data);
    } catch (err) {
      console.warn('[useStageFullVideos] list fetch failed', err);
    }
  }, [isCurrent]);

  const timersRef = useRef<Map<string, number>>(new Map());
  const inFlightRef = useRef<Set<string>>(new Set());

  // Must run its cleanup before the reload effect below re-fetches for a new stage.
  useEffect(() => {
    return () => {
      for (const timer of timersRef.current.values()) window.clearInterval(timer);
      timersRef.current.clear();
      inFlightRef.current.clear();
    };
  }, [eventId, stageIndex]);

  useEffect(() => {
    setViews({});
    void load();
  }, [eventId, stageIndex, load]);

  useEffect(() => {
    const timers = timersRef.current;
    const activeIds = new Set(
      Object.entries(views)
        .filter(([, v]) => isActive(v))
        .map(([id]) => id),
    );

    for (const [id, timer] of timers) {
      if (!activeIds.has(id)) {
        window.clearInterval(timer);
        timers.delete(id);
      }
    }

    for (const id of activeIds) {
      if (timers.has(id)) continue;
      const timer = window.setInterval(() => {
        const { eventId: e, stageIndex: s } = paramsRef.current;
        if (e == null || s == null) return;
        if (inFlightRef.current.has(id)) return;
        inFlightRef.current.add(id);
        getFullVideo(e, s, id)
          .then((view) => setView(id, view, { eventId: e, stageIndex: s }))
          .catch((err) => console.warn('[useStageFullVideos] per-car poll failed', id, err))
          .finally(() => inFlightRef.current.delete(id));
      }, POLL_MS);
      timers.set(id, timer);
    }
  }, [views, setView]);

  return { views, setView, reload: load };
}

export default useStageFullVideos;
